import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CrawlService, CrawlStatus, StartCrawlOptions, FailedPage } from '../../services/crawl';
import { Subscription, interval, firstValueFrom } from 'rxjs';

interface DomainCrawlStatus {
  domain: string;
  status: CrawlStatus | null;
  loading: boolean;
  error: string | null;
}

@Component({
  selector: 'app-crawl-management',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './crawl-management.html',
  styleUrl: './crawl-management.css'
})
export class CrawlManagementComponent implements OnInit, OnDestroy {
  domains: string[] = [];
  domainStatuses: Map<string, DomainCrawlStatus> = new Map();
  loadingDomains = false;
  error: string | null = null;
  
  failedPages: Map<string, FailedPage[]> = new Map();
  failedPagesLoading: Map<string, boolean> = new Map();
  failedPagesExpanded: Map<string, boolean> = new Map();

  private pollingSubscriptions: Map<string, Subscription> = new Map();
  private readonly POLL_INTERVAL = 10000; // 10 seconds

  constructor(private crawlService: CrawlService) {}

  ngOnInit() {
    this.loadDomains();
  }

  ngOnDestroy() {
    // Clean up all polling subscriptions
    this.pollingSubscriptions.forEach(sub => sub.unsubscribe());
    this.pollingSubscriptions.clear();
  }

  async loadDomains() {
    this.loadingDomains = true;
    this.error = null;

    try {
      const response = await firstValueFrom(this.crawlService.getDomains());
      if (response?.success && response.data) {
        this.domains = response.data.sort();
        
        // Initialize status for each domain
        this.domains.forEach(domain => {
          if (!this.domainStatuses.has(domain)) {
            this.domainStatuses.set(domain, {
              domain,
              status: null,
              loading: false,
              error: null
            });
          }
        });

        // Load status for all domains
        this.domains.forEach(domain => {
          this.loadCrawlStatus(domain);
        });
      }
    } catch (error: any) {
      console.error('Failed to load domains:', error);
      this.error = error.message || 'Failed to load domains';
    } finally {
      this.loadingDomains = false;
    }
  }

  async loadCrawlStatus(domain: string) {
    const domainStatus = this.domainStatuses.get(domain);
    if (!domainStatus) return;

    domainStatus.loading = true;
    domainStatus.error = null;

    try {
      const status = await firstValueFrom(this.crawlService.getCrawlStatus(domain));
      if (status) {
        domainStatus.status = status;
        
        // Start polling if crawl is in progress
        if (!this.isCrawlComplete(status)) {
          this.startPolling(domain);
        } else {
          this.stopPolling(domain);
        }
      }
    } catch (error: any) {
      console.error(`Failed to load crawl status for ${domain}:`, error);
      domainStatus.error = error.message || 'Failed to load crawl status';
    } finally {
      domainStatus.loading = false;
    }
  }

  async startCrawl(domain: string) {
    const domainStatus = this.domainStatuses.get(domain);
    if (!domainStatus) return;

    domainStatus.loading = true;
    domainStatus.error = null;

    try {
      const response = await firstValueFrom(this.crawlService.startCrawl(domain, {
        limit: 750,
        priority: 'high',
        frequency: 'manual'
      }));

      if (response?.success) {
        // Reload status after starting crawl
        await this.loadCrawlStatus(domain);
      } else {
        domainStatus.error = response?.message || 'Failed to start crawl';
      }
    } catch (error: any) {
      console.error(`Failed to start crawl for ${domain}:`, error);
      domainStatus.error = error.error?.message || error.message || 'Failed to start crawl';
    } finally {
      domainStatus.loading = false;
    }
  }

  startPolling(domain: string) {
    // Stop existing polling if any
    this.stopPolling(domain);

    // Start new polling
    const subscription = interval(this.POLL_INTERVAL).subscribe(() => {
      this.loadCrawlStatus(domain);
    });

    this.pollingSubscriptions.set(domain, subscription);
  }

  stopPolling(domain: string) {
    const subscription = this.pollingSubscriptions.get(domain);
    if (subscription) {
      subscription.unsubscribe();
      this.pollingSubscriptions.delete(domain);
    }
  }

  isCrawlComplete(status: CrawlStatus | null): boolean {
    if (!status) return false;
    if (status.phase === 'complete' || status.isComplete) return true;
    return false;
  }

  getProgressPercentage(status: CrawlStatus | null): number {
    if (!status || status.totalUrls === 0) return 0;
    return status.progress ?? Math.round(((status.pagesCrawled + status.pagesFailed) / status.totalUrls) * 100);
  }

  formatProgress(status: CrawlStatus | null): string {
    if (!status) return 'N/A';
    if (status.phase === 'analyzing') {
      const analyzed = status.pagesAnalyzed ?? 0;
      return `${status.pagesCrawled} crawled — analyzing ${analyzed} / ${status.totalUrls}`;
    }
    if (status.phase === 'complete') {
      const analyzed = status.pagesAnalyzed ?? status.pagesCrawled;
      return `${analyzed} / ${status.totalUrls} pages`;
    }
    const completed = status.pagesCrawled + status.pagesFailed;
    return `${completed} / ${status.totalUrls}`;
  }

  getStatusClass(status: CrawlStatus | null): string {
    if (!status) return 'status-unknown';
    if (this.isCrawlComplete(status)) return 'status-complete';
    if (status.phase === 'crawling' || status.phase === 'analyzing') return 'status-in-progress';
    if (status.pagesCrawled > 0 || status.pagesInQueue > 0) return 'status-in-progress';
    return 'status-pending';
  }

  getStatusText(status: CrawlStatus | null): string {
    if (!status) return 'Unknown';
    if (this.isCrawlComplete(status)) return 'Complete';
    if (status.phase === 'crawling') return 'Crawling';
    if (status.phase === 'analyzing') return 'Analyzing';
    if (status.pagesCrawled > 0 || status.pagesInQueue > 0) return 'In Progress';
    return 'Not Started';
  }

  async toggleFailedPages(domain: string) {
    const isExpanded = this.failedPagesExpanded.get(domain) || false;
    if (isExpanded) {
      this.failedPagesExpanded.set(domain, false);
      return;
    }

    if (!this.failedPages.has(domain)) {
      this.failedPagesLoading.set(domain, true);
      try {
        const pages = await firstValueFrom(this.crawlService.getFailedPages(domain));
        this.failedPages.set(domain, pages);
      } catch (error: any) {
        console.error(`Failed to load failed pages for ${domain}:`, error);
        this.failedPages.set(domain, []);
      } finally {
        this.failedPagesLoading.set(domain, false);
      }
    }

    this.failedPagesExpanded.set(domain, true);
  }
}

